/**
 * useHospitalAuth — Authentication hook for Hospital Portal
 */
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { resolveHospitalProfile, updateHospitalLastLogin, type HospitalProfile } from '../services/hospitalAuthService';

interface HospitalAuthState {
  profile: HospitalProfile | null;
  loading: boolean;
  isAuthenticated: boolean;
}

export function useHospitalAuth(): HospitalAuthState {
  const [profile, setProfile] = useState<HospitalProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const resolve = async (userId: string) => {
      try {
        const p = await resolveHospitalProfile(userId);
        if (mounted) {
          setProfile(p);
          if (p) updateHospitalLastLogin(userId);
        }
      } catch {
        if (mounted) setProfile(null);
      }
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_, session) => {
      if (!mounted) return;
      if (session?.user) {
        resolve(session.user.id);
      } else {
        setProfile(null);
      }
    });

    const init = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!mounted) return;
        if (session?.user) {
          await resolve(session.user.id);
        }
      } catch {
        // ignore
      } finally {
        if (mounted) setLoading(false);
      }
    };
    init();

    return () => { mounted = false; subscription.unsubscribe(); };
  }, []);

  return { profile, loading, isAuthenticated: !!profile };
}
